import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { HeartHandshake, Gift, HandHeart, Ticket } from "lucide-react";

export default function AssocResources() {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/associations/me").then((r) => {
      const a = r.data.association;
      setFull({
        name: a.name || "", category: a.category || "autre", description: a.description || "",
        presentation: a.presentation || "", logo_url: a.logo_url || "", cover_url: a.cover_url || "",
        gallery: a.gallery || [], email: a.email || "", phone: a.phone || "", website: a.website || "",
        address: a.address || "", postal_code: a.postal_code || "", city: a.city || "",
        facebook_url: a.facebook_url || "", instagram_url: a.instagram_url || "",
        membership_info: a.membership_info || "", join_url: a.join_url || "", donation_url: a.donation_url || "",
        helloasso_url: a.helloasso_url || "",
        looking_for_volunteers: !!a.looking_for_volunteers,
      });
    }).catch(() => {});
  }, []);
  const set = (k, v) => setFull((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/associations/me", full); toast.success("Ressources enregistrées !"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur à l'enregistrement."); }
    finally { setBusy(false); }
  };

  if (!full) return <div className="p-6 text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-3xl" data-testid="assoc-resources">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Ressources de l'association</h1>
      <p className="text-slate-500 text-sm mt-1">Adhésion, appel aux bénévoles et dons : ces éléments apparaissent sur votre page publique.</p>

      <div className="mt-6 space-y-6">
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><HeartHandshake size={18} className="text-emerald-700" /> Adhésion</h2>
          <div className="space-y-3">
            <div><Label>Informations d'adhésion</Label><Textarea rows={3} value={full.membership_info} onChange={(e) => set("membership_info", e.target.value)} placeholder="Cotisation, modalités d'inscription, période…" data-testid="assoc-res-membership" /></div>
            <div><Label>Lien d'adhésion / contact</Label><Input value={full.join_url} onChange={(e) => set("join_url", e.target.value)} placeholder="https://… ou mailto:" data-testid="assoc-res-joinurl" /></div>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><HandHeart size={18} className="text-emerald-700" /> Appel aux bénévoles</h2>
          <div className="flex items-center justify-between">
            <div>
              <Label className="mb-0">Nous recherchons des bénévoles</Label>
              <p className="text-xs text-slate-500 mt-1">Affiche un bouton de contact sur votre page publique.</p>
            </div>
            <Switch checked={full.looking_for_volunteers} onCheckedChange={(v) => set("looking_for_volunteers", v)} data-testid="assoc-res-volunteers" />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Gift size={18} className="text-emerald-700" /> Dons</h2>
          <div><Label>Lien de don</Label><Input value={full.donation_url} onChange={(e) => set("donation_url", e.target.value)} placeholder="https://… (HelloAsso, cagnotte…)" data-testid="assoc-res-donation" /></div>
          <p className="text-xs text-slate-500 mt-2">Collez le lien de votre plateforme de dons (HelloAsso, cagnotte en ligne, etc.).</p>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Ticket size={18} className="text-emerald-700" /> Page HelloAsso</h2>
          <div><Label>Lien de votre page HelloAsso</Label><Input value={full.helloasso_url} onChange={(e) => set("helloasso_url", e.target.value)} placeholder="https://www.helloasso.com/associations/votre-asso" data-testid="assoc-res-helloasso" /></div>
          <p className="text-xs text-slate-500 mt-2">Si vous utilisez HelloAsso, collez le lien de votre page : un bouton <b>« Adhérer / Billetterie »</b> apparaîtra sur votre vitrine publique.</p>
        </section>

        <div className="flex justify-end sticky bottom-0 bg-slate-50 py-3">
          <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="assoc-res-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
        </div>
      </div>
    </div>
  );
}
